// ── Notification + sound preferences (localStorage-backed) ────────
//
// Vendor-toggleable in Settings; consumed by useWhatsAppRealtime
// when a `wa:message:new` event arrives.
//
// localStorage instead of server-side per-user because:
//   • This is a per-device preference (silenced on shared kiosk,
//     loud on agent's laptop). Server-side per-user would force the
//     same setting across all devices, which is the opposite of what
//     a desk agent + on-call agent want.
//   • Zero backend round-trip on read — settings panel + hook both
//     read synchronously.

const PREFIX = 'wa.realtime.prefs:';
const NOTIFICATIONS_KEY = `${PREFIX}browserNotifications`;
const SOUND_KEY = `${PREFIX}soundAlerts`;

export interface RealtimePrefs {
  browserNotifications: boolean;
  soundAlerts: boolean;
}

const DEFAULTS: RealtimePrefs = {
  // Off-by-default — vendors must explicitly opt in (matches the
  // browser's own permission-on-action model and avoids surprising
  // anyone who just logged in).
  browserNotifications: false,
  soundAlerts: false,
};

function readBool(key: string, fallback: boolean): boolean {
  try {
    const raw = localStorage.getItem(key);
    if (raw == null) return fallback;
    return raw === 'true';
  } catch {
    return fallback;
  }
}

function writeBool(key: string, value: boolean): void {
  try {
    localStorage.setItem(key, value ? 'true' : 'false');
  } catch {
    /* localStorage may be disabled in private mode — silent fallback */
  }
}

export function getRealtimePrefs(): RealtimePrefs {
  return {
    browserNotifications: readBool(NOTIFICATIONS_KEY, DEFAULTS.browserNotifications),
    soundAlerts: readBool(SOUND_KEY, DEFAULTS.soundAlerts),
  };
}

export function setBrowserNotificationsEnabled(value: boolean): void {
  writeBool(NOTIFICATIONS_KEY, value);
}

export function setSoundAlertsEnabled(value: boolean): void {
  writeBool(SOUND_KEY, value);
}

/**
 * Subscribe to preference changes from THIS tab. localStorage's native
 * `storage` event only fires for OTHER tabs — within the same tab we
 * dispatch a synthetic CustomEvent so settings UI ↔ hook stay in sync
 * without a page reload.
 */
const PREF_CHANGE_EVT = 'wa-realtime-prefs-change';

export function broadcastPrefsChange(): void {
  try {
    window.dispatchEvent(new CustomEvent(PREF_CHANGE_EVT));
  } catch {
    /* no-op */
  }
}

export function onPrefsChange(handler: () => void): () => void {
  const same = () => handler();
  const other = (e: StorageEvent) => {
    if (e.key && e.key.startsWith(PREFIX)) handler();
  };
  window.addEventListener(PREF_CHANGE_EVT, same);
  window.addEventListener('storage', other);
  return () => {
    window.removeEventListener(PREF_CHANGE_EVT, same);
    window.removeEventListener('storage', other);
  };
}

// ── Browser notification permission ────────────────────────────────

export function getBrowserNotificationPermission():
  | 'granted'
  | 'denied'
  | 'default'
  | 'unsupported' {
  if (typeof Notification === 'undefined') return 'unsupported';
  return Notification.permission;
}

export async function requestBrowserNotificationPermission(): Promise<
  'granted' | 'denied' | 'default' | 'unsupported'
> {
  if (typeof Notification === 'undefined') return 'unsupported';
  if (Notification.permission === 'granted' || Notification.permission === 'denied') {
    return Notification.permission;
  }
  try {
    const result = await Notification.requestPermission();
    return result;
  } catch {
    return 'denied';
  }
}

// ── Sound alert ────────────────────────────────────────────────────
//
// Plays a short WAV/MP3 ping. We avoid bundling an audio file by
// generating a brief beep with the Web Audio API — works offline, no
// asset to manage, and respects the browser's autoplay rules (it
// only plays after a user interaction in the current tab).

let audioCtx: AudioContext | null = null;

function ensureAudioContext(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  // Browser-prefixed fallback for older Safari builds.
  const Ctor: typeof AudioContext | undefined =
    (window as any).AudioContext || (window as any).webkitAudioContext;
  if (!Ctor) return null;
  if (!audioCtx) audioCtx = new Ctor();
  return audioCtx;
}

export function playInboundPing(): void {
  const ctx = ensureAudioContext();
  if (!ctx) return;
  if (ctx.state === 'suspended') {
    // Will resume on next user interaction; nothing to do.
    try {
      void ctx.resume();
    } catch {
      /* swallow */
    }
  }
  try {
    const now = ctx.currentTime;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(880, now);
    osc.frequency.exponentialRampToValueAtTime(660, now + 0.12);
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(0.15, now + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.18);
    osc.connect(gain).connect(ctx.destination);
    osc.start(now);
    osc.stop(now + 0.2);
  } catch {
    /* swallow — audio is a nice-to-have */
  }
}
